import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a billing/subscription date using UTC to match Stripe exactly.
 * Stripe stores dates as UTC timestamps, so we must display in UTC
 * to avoid timezone-induced off-by-one errors.
 */
export function formatBillingDate(
  dateStr: string,
  options?: { includeYear?: boolean }
): string {
  const d = new Date(dateStr);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[d.getUTCMonth()];
  const day = d.getUTCDate();
  if (options?.includeYear) {
    return `${month} ${day}, ${d.getUTCFullYear()}`;
  }
  return `${month} ${day}`;
}

/**
 * Detect the user's locale from the browser, with a safe fallback.
 * Runs only in the browser, falls back to 'en-US' on the server.
 */
const getUserLocale = (): string => {
  if (typeof navigator === "undefined") return "en-US";
  return navigator.language || (navigator.languages && navigator.languages[0]) || "en-US";
};

/**
 * Safely parse a date input. Returns null for null/undefined/empty/invalid values.
 */
const safeDate = (input: string | number | Date | null | undefined): Date | null => {
  if (input === null || input === undefined || input === "") return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
};

const FALLBACK_LABEL = "—";

/**
 * Format a request timestamp in the user's local timezone AND locale.
 * Long form for desktop, e.g. "Wednesday, 22 April 2026 at 14:30" (en-GB).
 * Returns a clear fallback for null/invalid values instead of "Invalid Date".
 */
export function formatRequestDateLong(dateStr: string | null | undefined): string {
  const d = safeDate(dateStr);
  if (!d) return FALLBACK_LABEL;
  try {
    return new Intl.DateTimeFormat(getUserLocale(), {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

/**
 * Compact form for mobile, e.g. "22 Apr, 14:30" (en-GB) or "22 avr., 14:30" (fr).
 */
export function formatRequestDateShort(dateStr: string | null | undefined): string {
  const d = safeDate(dateStr);
  if (!d) return FALLBACK_LABEL;
  try {
    return new Intl.DateTimeFormat(getUserLocale(), {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

/**
 * Date-only locale-aware format, e.g. "22 Apr 2026" (en-GB) or "22 avr. 2026" (fr).
 * Used for lists where time of day is not meaningful (invites, referrals).
 */
export function formatDateMedium(dateStr: string | null | undefined): string {
  const d = safeDate(dateStr);
  if (!d) return FALLBACK_LABEL;
  try {
    return new Intl.DateTimeFormat(getUserLocale(), {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(d);
  } catch {
    return d.toLocaleDateString();
  }
}
